import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@mui/material';
import { useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import './ProfileWantListTab.css';

interface ProfileWantListTabProps {
  username: string;
}

// Get card image URL with fallback
const getCardImageUrl = (item: CollectionList): string => {
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;

  if (item.card?.imageUris?.normal) {
    return item.card.imageUris.normal;
  }
  if (item.card?.imageUris?.small) {
    return item.card.imageUris.small;
  }
  if (item.card?.imageUris?.large) {
    return item.card.imageUris.large;
  }
  if (item.card?.normal_fallbacks?.[0]) {
    return item.card.normal_fallbacks[0];
  }

  return defaultImage;
};

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Card item component
const WantListCardItem: React.FC<{ item: CollectionList }> = ({ item }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
  const imageUrl = getCardImageUrl(item);

  const cardName = item.card?.name || item.cardName || 'Unknown Card';

  const handleClick = () => {
    const scryfallId = item.card?.scryfallID;
    if (scryfallId) {
      navigate(`/cards/${scryfallId}`);
    }
  };

  return (
    <div className="profile-want-card-item" onClick={handleClick}>
      <div className="profile-want-card-image-container">
        <img
          src={imageError ? defaultImage : imageUrl}
          alt={cardName}
          className="profile-want-card-image"
          onError={() => setImageError(true)}
          loading="lazy"
        />
        {item.quantity > 1 && (
          <div className="profile-want-card-quantity">x{item.quantity}</div>
        )}
      </div>
      <div className="profile-want-card-info">
        <span className="profile-want-card-name" title={cardName}>
          {cardName}
        </span>
        {item.displayPrice != null && (
          <Points points={item.displayPrice} type="small" />
        )}
      </div>
    </div>
  );
};

// Loading skeleton
const WantListSkeleton: React.FC = () => (
  <div className="profile-want-skeleton">
    <div className="profile-want-header">
      <Skeleton variant="text" width={120} height={28} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    </div>
    <div className="profile-want-grid">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="profile-want-card-item">
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              aspectRatio: '488 / 680',
              borderRadius: '8px',
            }}
          />
          <Skeleton variant="text" width="80%" height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        </div>
      ))}
    </div>
  </div>
);

export const ProfileWantListTab: React.FC<ProfileWantListTabProps> = ({ username }) => {
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);

  const { data, isLoading, isFetching } = useFetchPublicWantListQuery({
    username,
    page,
    limit: 48,
  });

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.wantList) {
      setPreviousPages((prev) => [...prev, ...data.wantList]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
  }, [username]);

  if (isLoading) {
    return <WantListSkeleton />;
  }

  // Combine previous pages with current page data
  const allItems = [...previousPages, ...(data?.wantList || [])];

  if (!allItems.length) {
    return (
      <div className="profile-want-empty">
        <p>This user's want list is empty.</p>
      </div>
    );
  }

  const totalCards = data?.totalCards || 0;
  const totalValue = data?.totalValue || 0;
  const hasMore = data?.hasMore || false;

  return (
    <div className="profile-want-tab">
      <div className="profile-want-header">
        <span className="profile-want-count">{formatNumber(totalCards)} Cards</span>
        {totalValue > 0 && <Points points={totalValue} />}
      </div>

      <div className="profile-want-grid">
        {allItems.map((item) => (
          <WantListCardItem key={item.id} item={item} />
        ))}
      </div>

      {hasMore && (
        <div className="profile-want-load-more">
          <MagicButton
            label={isFetching ? 'Loading...' : 'Load More'}
            color="dark-grey"
            onPress={handleLoadMore}
            isLoading={isFetching}
            isDisabled={isFetching}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileWantListTab;
